import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';

// Import stores
import useAuthStore from './store/useAuthStore';
import useCookieStore from './store/useCookieStore';

// Import pages
import ExchangePage from './pages/ExchangePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import KYCPage from './pages/KYCPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import MarketPage from './pages/MarketPage';
import P2PPage from './pages/P2PPage';
import ProfilePage from './pages/ProfilePage';
import QuickSwapPage from './pages/QuickSwapPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyOTPPage from './pages/VerifyOTPPage';
import WalletPage from './pages/WalletPage';

// Import P2P components
import P2POrderDetail from './components/p2p/P2POrderDetail';
import MarketplaceOrderDetail from './components/p2p/marketplace/MarketplaceOrderDetail';

// Import admin pages
import BotsPage from './pages/admin/BotsPage';
import CoinsPairsPage from './pages/admin/CoinsPairsPage';
import DepositsPage from './pages/admin/DepositsPage';
import KeeperPage from './pages/admin/KeeperManagementPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import OrdersPage from './pages/admin/OrdersPage';
import AdminQuickSwapPage from './pages/admin/QuickSwapManagementPage';
import SettingsPage from './pages/admin/SettingsPage';
import SystemPage from './pages/admin/SystemManagementPage';
import TradingPage from './pages/admin/TradingPage';
import UsersPage from './pages/admin/UsersPage';
import WithdrawalsPage from './pages/admin/WithdrawalsPage';
import WalletLedgerPage from './pages/admin/WalletLedgerPage';
import MarketplaceOrdersPage from './pages/admin/MarketplaceOrdersPage';
import ResourcesPage from './pages/admin/ResourcesPage';
import AdminDashboard from './pages/AdminDashboard';

// Import components
import AdminRoute from './components/admin/AdminRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { Header, Footer } from './components/layout';
import CookieConsentModal from './components/modals/CookieConsentModal';
import TermsOfUsePage from './pages/TermsOfUsePage';

// Import styles
import InstallPromptComponent from './components/InstallPromptComponent';
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function CustomerOnly({ children }) {
  const location = useLocation();
  return location.pathname.startsWith('/admin') ? null : children;
}

// Component to handle conditional footer rendering
function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  return (
    <>
      {!isAdminRoute && <Header />}
      <main className={isAdminRoute ? 'flex-1' : 'app-content'}>
        {!location.pathname.startsWith('/admin') && <InstallPromptComponent />}
        <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-otp" element={<VerifyOTPPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:uidb64/:token" element={<ResetPasswordPage />} />
              <Route path="/terms" element={<TermsOfUsePage />} />
              <Route path="/privacy" element={<Navigate to="/terms#privacy-policy" replace />} />

              {/* Public routes */}
              <Route path="/swap" element={<QuickSwapPage />} />
              <Route path="/trade" element={<ExchangePage />} />
              <Route path="/markets" element={<MarketPage />} />

              {/* Protected routes */}
              <Route path="/wallet" element={
                <ProtectedRoute>
                  <WalletPage />
                </ProtectedRoute>
              } />
              <Route path="/p2p" element={
                <ProtectedRoute>
                  <P2PPage />
                </ProtectedRoute>
              } />
              <Route path="/p2p/order/:orderId" element={
                <ProtectedRoute>
                  <P2POrderDetail />
                </ProtectedRoute>
              } />
              <Route path="/p2p/order/:orderId/chat" element={
                <ProtectedRoute>
                  <P2POrderDetail />
                </ProtectedRoute>
              } />
              <Route path="/p2p/marketplace/order/:orderId" element={
                <ProtectedRoute>
                  <MarketplaceOrderDetail />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/kyc" element={
                <ProtectedRoute>
                  <KYCPage />
                </ProtectedRoute>
              } />

              {/* Admin routes */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
              <Route path="/admin/resources" element={<AdminRoute><ResourcesPage /></AdminRoute>} />
              <Route path="/admin/resources/:app/:model" element={<AdminRoute><ResourcesPage /></AdminRoute>} />
              <Route path="/admin/users" element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              } />
              <Route path="/admin/orders" element={
                <AdminRoute>
                  <OrdersPage />
                </AdminRoute>
              } />
              <Route path="/admin/withdrawals" element={
                <AdminRoute>
                  <WithdrawalsPage />
                </AdminRoute>
              } />
              <Route path="/admin/deposits" element={
                <AdminRoute>
                  <DepositsPage />
                </AdminRoute>
              } />
              <Route path="/admin/wallet-ledger" element={
                <AdminRoute>
                  <WalletLedgerPage />
                </AdminRoute>
              } />
              <Route path="/admin/marketplace-orders" element={
                <AdminRoute>
                  <MarketplaceOrdersPage />
                </AdminRoute>
              } />
              <Route path="/admin/coins-pairs" element={
                <AdminRoute>
                  <CoinsPairsPage />
                </AdminRoute>
              } />
              <Route path="/admin/trading" element={
                <AdminRoute>
                  <TradingPage />
                </AdminRoute>
              } />
              <Route path="/admin/keeper" element={
                <AdminRoute>
                  <KeeperPage />
                </AdminRoute>
              } />
              <Route path="/admin/settings" element={
                <AdminRoute>
                  <SettingsPage />
                </AdminRoute>
              } />
              <Route path="/admin/bots" element={
                <AdminRoute>
                  <BotsPage />
                </AdminRoute>
              } />
              <Route path="/admin/notifications" element={
                <AdminRoute>
                  <NotificationsPage />
                </AdminRoute>
              } />
              <Route path="/admin/quick-swap" element={
                <AdminRoute>
                  <AdminQuickSwapPage />
                </AdminRoute>
              } />
              <Route path="/admin/system" element={
                <AdminRoute>
                  <SystemPage />
                </AdminRoute>
              } />

              {/* Redirect unknown routes to landing page */}
              <Route path="*" element={<Navigate to="/markets" replace />} />
            </Routes>
          </main>

          {/* Footer - Hide on admin pages */}
          {!isAdminRoute && <Footer />}
        </>
      );
}

function App() {
  const { checkAuth } = useAuthStore();
  const { cookiesAccepted, showCookieModal, acceptCookies, hideCookieModal } = useCookieStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Global error handler for MetaMask and other third-party errors
  useEffect(() => {
    const handleGlobalError = (event) => {
      // Handle MetaMask errors
      if (event.error && event.error.message &&
        (event.error.message.includes('MetaMask') ||
          event.error.message.includes('Failed to connect to MetaMask'))) {
        console.warn('MetaMask error caught and ignored:', event.error.message);
        event.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Handle MetaMask promise rejections
      if (event.reason && event.reason.message &&
        (event.reason.message.includes('MetaMask') ||
          event.reason.message.includes('Failed to connect to MetaMask'))) {
        console.warn('MetaMask promise rejection caught and ignored:', event.reason.message);
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="app-layout">
          <AppContent />

          {/* Cookie Consent Modal */}
          <CustomerOnly><CookieConsentModal
            isOpen={showCookieModal && !cookiesAccepted}
            onClose={hideCookieModal}
            onAccept={acceptCookies}
          /></CustomerOnly>

          {/* Toast notifications */}
          <Toaster
            position="top-center"
            reverseOrder={false}
            gutter={8}
            containerClassName=""
            containerStyle={{
              top: '150px', // Position below navbar
            }}
            toastOptions={{
              duration: 5000,
              style: {
                background: '#10B981',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '500',
                padding: '12px 16px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                zIndex: 9999,
              },
              success: {
                duration: 5000,
                style: {
                  background: '#10B981',
                  color: '#fff',
                },
              },
              error: {
                duration: 5000,
                style: {
                  background: '#EF4444',
                  color: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
